import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppSettings } from '../app-settings';
import { CurrentUser, CurrentAcademicUser, Option, AuthenticationService, HeartbeatResult, AuthenticatedIdentity, Authenticated, TokenInfo, AuthenticationType, AcademicTitleId, AuthResult, AppVersionService } from '../domain';
import { ReplaySubject, Observable, Subscription, interval, forkJoin } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { ConsoleService } from '../widgets/console.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticationModalComponent } from 'app/main-core/authentication-modal/authentication-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as PB_CONSTANTS from '../shared/constants';

const HEARTBEAT_INTERVAL_IN_SECONDS = 60;

export class AuthDetails {
	constructor(
		public readonly idToken: string,
		public readonly accessToken: string
	){}
}

@Injectable()
export class AuthenticationServiceImpl implements AuthenticationService {
	
	private readonly _currentIdentitySubject = new ReplaySubject<Option<AuthenticatedIdentity>>(1);
	public readonly currentIdentity: Observable<Option<AuthenticatedIdentity>>;
	private _currentUser: Option<CurrentUser> = Option.create<CurrentUser>();
	private authenticated: boolean = false;
	private _heartbeatSubscription: Subscription;

	constructor(
		private console: ConsoleService,
		private appSettings: AppSettings,
		private http: HttpClient,
		private snackBar: MatSnackBar,
		private matDialog: MatDialog,
		private router: Router,
		private appVersionService: AppVersionService
	){
		this.currentIdentity = this._currentIdentitySubject.asObservable();
		this.currentIdentity.subscribe(identity => {
			this._currentUser = identity.map(i => i.user);

			identity
			.matchDo(
				() => {
					this.authenticated = false;
					this.console.info('[Identity cleared]');
					AuthenticationModalComponent.open(this.matDialog);
				},
				(i: AuthenticatedIdentity) => {
					this.authenticated = true;
					this.console.info('[Detected a new identity] -> ', i);
					setTimeout(() => {
						//calling snackbar in this way avoids an `ExpressionChangedAfterItHasBeenCheckedError`
						snackBar.open(`Hello, ${i.user.firstName}!`, null, {duration: 5000});
					},0);
					this.sendUserOff(i.user);
				});
		});
	}

	public config(): Promise<null>{
		return Promise.resolve(null);
	}

	public get isAuthenticated(): boolean {
		return this.authenticated;
	}

	public accept(a: Authenticated): void {
		this._currentIdentitySubject.next(Option.create(new AuthenticatedIdentity(a.tokenInfo.token, a.tokenInfo.tokenTimeoutDate, a.user)));
		window.localStorage.setItem(PB_CONSTANTS.LOCAL_STORAGE_PB_USER_TOKEN, a.tokenInfo.token);
		window.localStorage.setItem(PB_CONSTANTS.LOCAL_STORAGE_PB_USER_TOKEN_DATE, a.tokenInfo.tokenTimeoutDate.toString());
		this.startHeartbeat();
	}

	public tryAuthenticate(authResult: AuthResult, authType: AuthenticationType): Promise<boolean> {

		window.localStorage.setItem(PB_CONSTANTS.LOCAL_STORAGE_PB_USER_CLAIMS, JSON.stringify(authResult));

		let endpoint: string | null = null;
		
		if (authType === AuthenticationType.Okta) {
			endpoint = this.appSettings.APIURL + '/authentication/okta_accept/';
		} else if (authType === AuthenticationType.AzureAD) {
			endpoint = this.appSettings.APIURL + '/authentication/azure_accept/';
		}

		if (endpoint) {
			return new Promise((resolve) => {
				this.http.post(
						endpoint,
						new AuthDetails(
							authResult.idToken,
							authResult.accessToken
						)
					)
					.subscribe(
						json => {
							this.accept(Authenticated.fromJson(json));
							this.startHeartbeat();
							resolve(true);
						},
						_ => {
							this.console.log(`ERROR authenticating via ${authType === AuthenticationType.Okta ? 'Okta' : 'Azure AD'}`, authResult.name, authResult.username, authResult.employeeId);
							resolve(false);
						});
			});
		}		
	}

    public tryGetCurrentAcademicUserSync(): Option<CurrentAcademicUser>{
		return this._currentUser
			.bind(cu => cu.tryGetCurrentAcademicUser());
    }

	public clearCurrentUser(): void {
		this._currentIdentitySubject.next(Option.create<AuthenticatedIdentity>());
		window.localStorage.removeItem(PB_CONSTANTS.LOCAL_STORAGE_PB_USER_TOKEN);
		window.localStorage.removeItem(PB_CONSTANTS.LOCAL_STORAGE_PB_USER_TOKEN_DATE);
		window.localStorage.removeItem(PB_CONSTANTS.LOCAL_STORAGE_PB_USER_CLAIMS);
		window.sessionStorage.removeItem(PB_CONSTANTS.SESSION_STORAGE_SHOW_AZURE_LOGIN);
		this.stopHeartbeat();
	}

	public tryAuthenticateViaLocalStorage(): Promise<boolean>{
		return new Promise(resolve => {
			let token = window.localStorage.getItem(PB_CONSTANTS.LOCAL_STORAGE_PB_USER_TOKEN);
			let tokenTimeoutDate = new Date(window.localStorage.getItem(PB_CONSTANTS.LOCAL_STORAGE_PB_USER_TOKEN_DATE));
			if(!token){
				resolve(false);
				return;
			}
			this.http.get(this.appSettings.APIURL + '/authentication/heartbeat',
			{
				headers: new HttpHeaders({ 'Authorization': 'Bearer ' + token })
			})
			.pipe(map(data => HeartbeatResult.fromJson(data)))
			.subscribe(
				heartbeat => {
					heartbeat.authenticatedUser
					.map((cu:CurrentUser) => new Authenticated(new TokenInfo(token, tokenTimeoutDate), cu, heartbeat.currentApplicationVersion))
					.matchDo(
						() => {
							resolve(false);
						},
						(a: Authenticated) =>  {
							this.accept(a);
							this.startHeartbeat();
							resolve(true);
						});
				},
				err => {
					this.console.log('ERROR authenticating token from memory: ', err);
					resolve(false);
				});
		});
	}

	private _findUserByEmployeeId(employeeId): Promise<CurrentUser> {
		return new Promise((resolve) => {
			this.http.get(this.appSettings.APIURL + '/user/employeeid/' + employeeId)
				.subscribe(
					json =>
					{
						resolve(CurrentUser.fromJson(json));
					},
					_ => {
						this.console.log('ERROR getting authenticated user');
						resolve(null);
					});
		});
	}

	private startHeartbeat(): void {
		
		if (this._heartbeatSubscription) {
			return; //Only start the heartbeat once, no matter how many times this method is called.
		}

		this.console.info('[Heartbeat STARTED]');

		this._heartbeatSubscription = interval(HEARTBEAT_INTERVAL_IN_SECONDS * 1000).pipe(
			startWith(0), // Start immediately
			switchMap(() => {
				this.console.info('[HEARTBEAT]');
				return forkJoin({
					heartbeatResponse: this.http.get(`${this.appSettings.APIURL}/authentication/heartbeat`),
					uiVersion: this.appVersionService.fetchUiVersion()
				});
			}),
			map(({ heartbeatResponse, uiVersion }) => ({
				heartbeat: HeartbeatResult.fromJson(heartbeatResponse),
				uiVersion
			}))
		).subscribe({
			next: ({ heartbeat, uiVersion }) => {
				this.handleHeartbeat(heartbeat, uiVersion);
			},
			error: err => {
				this.console.error('HEARTBEAT ERROR:', err);
				this.clearCurrentUser();
			}
		});
	}

	private handleHeartbeat(heartbeat: HeartbeatResult, uiVersion: string): void {
		
		heartbeat.authenticatedUser.matchDo(
			() => this.clearCurrentUser(),
			user => {
				this._currentUser
					.filter(cu => cu.id !== user.id)
					.doWithValue(() => this.clearCurrentUser());
			}
		);

		const storedServerVersion = localStorage.getItem(PB_CONSTANTS.LOCAL_STORAGE_PB_SERVER_VERSION);
		const storedUiVersion = localStorage.getItem(PB_CONSTANTS.LOCAL_STORAGE_PB_UI_VERSION);

		const currentServerVersion = heartbeat.currentApplicationVersion ?? '';
		const currentUiVersion = uiVersion ?? '';

		const needsServerRefresh = storedServerVersion === null || (storedServerVersion !== currentServerVersion);
		const needsUiRefresh = storedUiVersion === null || (storedUiVersion !== currentUiVersion);

		if (needsServerRefresh || needsUiRefresh) {
			if (needsServerRefresh) localStorage.setItem(PB_CONSTANTS.LOCAL_STORAGE_PB_SERVER_VERSION, currentServerVersion);
			if (needsUiRefresh) localStorage.setItem(PB_CONSTANTS.LOCAL_STORAGE_PB_UI_VERSION, currentUiVersion);
			window.location.reload();
			return;
		}
	}

	private stopHeartbeat(){
		if(!!this._heartbeatSubscription){
			this.console.info('[Heartbeat STOPPED]');
			this._heartbeatSubscription.unsubscribe();
			this._heartbeatSubscription = undefined;
		}
	}

	private sendUserOff(u: CurrentUser): void {
		let navigateIfNecessary = (routeUrl: string, ...otherAcceptableUrls: string[]) => {
			let acceptableUrls = [routeUrl].concat(otherAcceptableUrls);
			let currentUrlPath = new URL(window.location.href).pathname;
			this.console.info('Attempting to send the user on their way based on their roles', currentUrlPath, acceptableUrls);
			if(acceptableUrls.some(url => currentUrlPath.indexOf(url) == 0)){
				this.console.info('User is already in a relevant part of the application (no need to redirect them)')
				return;
			} else {
				this.console.info('User sent -> ' + routeUrl)
				this.router.navigate([routeUrl]);
			}
		};

		if (u.roles.academicHumanResources) { 
			navigateIfNecessary('/ahr','/academic','/review','/program-review'); 
		}
		else if (u.roles.civilRightsCompliance) { 
			navigateIfNecessary('/crc'); 
		}
		else if (u.roles.academic.hasValue()) { 

			if (u.roles.academic.value.title?.id  == AcademicTitleId.StaffDirector) {
				navigateIfNecessary('/academic','/review'); 
			} else {
				navigateIfNecessary('/academic','/program-review','/review'); 
			}
		}
		else { 
			navigateIfNecessary('/explore','/review');
		}
	}
}